import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputBase, Tooltip, useTheme } from '@mui/material';

type GuidanceTourStepSearchProps = {
  value: string;
  onChange: (value: string) => void;
};

export function GuidanceTourStepSearch({
  value,
  onChange,
}: GuidanceTourStepSearchProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const handleCollapse = () => {
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCollapse();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: 28,
        height: 28,
        flexShrink: 0,
      }}
    >
      <Tooltip title={open ? 'Close search' : 'Search steps'}>
        <IconButton
          size="small"
          onClick={() => {
            if (open) {
              handleCollapse();
              return;
            }
            setOpen(true);
          }}
          aria-label={open ? 'Close step search' : 'Search steps'}
          sx={{
            width: 28,
            height: 28,
            opacity: open ? 0 : 1,
            pointerEvents: open ? 'none' : 'auto',
            transition: theme.transitions.create('opacity', { duration: 150 }),
          }}
        >
          <SearchIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          width: open ? 136 : 28,
          height: 28,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          overflow: 'hidden',
          borderRadius: 1,
          border: open ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
          bgcolor: open ? 'background.paper' : 'transparent',
          boxShadow: open ? theme.shadows[1] : 'none',
          transition: theme.transitions.create(['width', 'opacity', 'border-color', 'box-shadow'], {
            duration: 200,
            easing: theme.transitions.easing.easeInOut,
          }),
        }}
      >
        <SearchIcon
          sx={{
            fontSize: 14,
            color: 'text.secondary',
            ml: 0.75,
            flexShrink: 0,
          }}
        />
        <InputBase
          inputRef={inputRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search"
          inputProps={{
            'aria-label': 'Search Launch Readiness steps',
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            px: 0.5,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            '& .MuiInputBase-input': {
              py: 0.375,
              height: 20,
            },
          }}
        />
        <IconButton
          size="small"
          onClick={handleCollapse}
          aria-label="Close step search"
          sx={{ width: 22, height: 22, mr: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
